
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { User, UserCircle, Users } from "lucide-react";

interface UserActivityData {
  date: string;
  active_users: number;
  new_users: number;
}

interface UserStats {
  total_users: number;
  new_users_this_month: number;
  active_users_count: number;
  active_rate: number;
}

export const UserActivityMetrics = () => {
  const [activityData, setActivityData] = useState<UserActivityData[]>([]);
  const [userStats, setUserStats] = useState<UserStats>({
    total_users: 0,
    new_users_this_month: 0,
    active_users_count: 0,
    active_rate: 0
  });

  useEffect(() => {
    fetchUserActivityData();
    fetchUserStats();
  }, []);

  const fetchUserActivityData = async () => {
    try {
      // Use our new RPC function
      const { data, error } = await supabase.rpc('get_user_activity_by_day', { days_range: 30 });

      if (error) throw error;
      
      // Data comes ready to use from our function
      setActivityData(data || []);
    } catch (error) {
      console.error('Error fetching user activity data:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      // Use our new RPC function
      const { data, error } = await supabase.rpc('get_total_users_count');
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setUserStats(data[0]);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">User Activity</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Users
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats.total_users}</div>
            <p className="text-xs text-muted-foreground">
              Registered accounts
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              New Users
            </CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats.new_users_this_month}</div>
            <p className="text-xs text-muted-foreground">
              This month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Users Rate
            </CardTitle>
            <UserCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats.active_rate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Users active in last 30 days
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>User Activity Trends</CardTitle>
          <CardDescription>Active and new users over the last 30 days</CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <ChartContainer 
            config={{ 
              "active": { theme: { light: "#0ea5e9", dark: "#38bdf8" } },
              "new": { theme: { light: "#10b981", dark: "#34d399" } }
            }} 
            className="h-[300px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<ChartTooltipContent />} />
                <Area 
                  type="monotone" 
                  dataKey="active_users" 
                  name="Active Users" 
                  stackId="1" 
                  stroke="var(--color-active)" 
                  fill="var(--color-active)" 
                  fillOpacity={0.6}
                />
                <Area 
                  type="monotone" 
                  dataKey="new_users" 
                  name="New Users" 
                  stackId="2" 
                  stroke="var(--color-new)" 
                  fill="var(--color-new)" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};
